import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Search, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SearchCriteria } from '@/pages/People';
import { LocationAutocomplete } from './LocationAutocomplete';

interface SearchFormProps {
  onSearch: (criteria: SearchCriteria) => void;
  isSearching: boolean;
  creditsRemaining?: number | null;
}

export const SearchForm = ({ onSearch, isSearching, creditsRemaining }: SearchFormProps) => {
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('');
  const [locationType, setLocationType] = useState<'exact' | 'radius'>('exact');
  const [radius, setRadius] = useState(25);
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [keywordsInName, setKeywordsInName] = useState('');
  const [keywordTags, setKeywordTags] = useState('');
  const [hasEmail, setHasEmail] = useState(true);
  const [hasPhone, setHasPhone] = useState(false);
  const [enrichEmails, setEnrichEmails] = useState(false);
  const [apiCredits, setApiCredits] = useState<number | null>(null);
  const [showCompanyFilters, setShowCompanyFilters] = useState(false);
  const [showContactFilters, setShowContactFilters] = useState(false);

  useEffect(() => {
    if (creditsRemaining !== null && creditsRemaining !== undefined) {
      setApiCredits(creditsRemaining);
    }
  }, [creditsRemaining]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!jobTitle || !location) {
      return;
    }

    onSearch({
      jobTitle,
      location,
      locationType,
      radius,
      companyName,
      industry,
      companySize,
      keywordsInName,
      keywordTags,
      hasEmail,
      hasPhone,
      enrichEmails,
    });
  };

  return (
    <Card className="p-6">
      {apiCredits !== null && (
        <Alert className="mb-6 bg-primary/10 border-primary/20">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription>
            Apollo.io Credits Remaining: <span className="font-bold">{apiCredits.toLocaleString()}</span>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Search Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title *</Label>
            <Input
              id="jobTitle"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g., Sales Manager"
              required
            />
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="enrichEmails"
                checked={enrichEmails}
                onCheckedChange={(checked) => setEnrichEmails(checked as boolean)}
              />
              <Label htmlFor="enrichEmails" className="font-normal cursor-pointer text-sm">
                Enrich Emails (uses Apollo credits to unlock email addresses)
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <LocationAutocomplete
              value={location}
              onChange={setLocation}
              placeholder="e.g., Boston, MA"
              required
            />
          </div>
        </div>

        {/* Location Type */}
        <div className="space-y-4">
          <Label>Location Search Type</Label>
          <RadioGroup value={locationType} onValueChange={(value: 'exact' | 'radius') => setLocationType(value)}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="exact" id="exact" />
              <Label htmlFor="exact" className="font-normal cursor-pointer">Exact Location</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="radius" id="radius" />
              <Label htmlFor="radius" className="font-normal cursor-pointer">Within Radius</Label>
            </div>
          </RadioGroup>

          {locationType === 'radius' && (
            <div className="space-y-2 pl-6">
              <Label>Distance Radius: {radius} miles</Label>
              <Slider
                value={[radius]}
                onValueChange={(value) => setRadius(value[0])}
                min={5}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>5 miles</span>
                <span>100 miles</span>
              </div>
            </div>
          )}
        </div>

        {/* Company Filters */}
        <Collapsible open={showCompanyFilters} onOpenChange={setShowCompanyFilters}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
            <span className="font-medium">Company Filters (Optional)</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showCompanyFilters ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Acme Corp"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., Technology"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywordsInName">Keywords in Name</Label>
              <Input
                id="keywordsInName"
                value={keywordsInName}
                onChange={(e) => setKeywordsInName(e.target.value)}
                placeholder="e.g., Restoration, Services"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywordTags">Keyword Tags</Label>
              <Input
                id="keywordTags"
                value={keywordTags}
                onChange={(e) => setKeywordTags(e.target.value)}
                placeholder="e.g., Disaster Restoration, Fire Damage"
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple tags with commas
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="companySize">Company Size</Label>
              <Input
                id="companySize"
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                placeholder="e.g., 50-200"
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Contact Info Filters */}
        <Collapsible open={showContactFilters} onOpenChange={setShowContactFilters}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
            <span className="font-medium">Contact Information Filters</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showContactFilters ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4 space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasEmail"
                checked={hasEmail}
                onCheckedChange={(checked) => setHasEmail(checked as boolean)}
              />
              <Label htmlFor="hasEmail" className="font-normal cursor-pointer">
                Must have email address
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasPhone"
                checked={hasPhone}
                onCheckedChange={(checked) => setHasPhone(checked as boolean)}
              />
              <Label htmlFor="hasPhone" className="font-normal cursor-pointer">
                Must have phone number
              </Label>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSearching || !jobTitle || !location}
        >
          <Search className="h-4 w-4 mr-2" />
          {isSearching ? 'Searching...' : 'Search Prospects'}
        </Button>
      </form>
    </Card>
  );
};
